import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONSTANTS } from '../../data';
import type { MemoryFragment } from '../../systems/MemoryFragmentSystem';

interface MemoryFragmentPopupProps {
  memoryFragment: MemoryFragment | null;
  onClose: () => void;
}

export const MemoryFragmentPopup: React.FC<MemoryFragmentPopupProps> = ({
  memoryFragment,
  onClose
}) => {
  const [isGlitching, setIsGlitching] = useState(false);

  useEffect(() => {
    if (memoryFragment) {
      // Start glitch effect
      setIsGlitching(true);
      
      // Auto-close after display duration
      const timer = setTimeout(() => {
        onClose();
      }, CONSTANTS.MEMORY_FRAGMENTS.displayDuration);

      // Stop glitch effect after initial glitch duration
      const glitchTimer = setTimeout(() => {
        setIsGlitching(false);
      }, CONSTANTS.MEMORY_FRAGMENTS.glitchDuration * 3);

      return () => {
        clearTimeout(timer);
        clearTimeout(glitchTimer);
      };
    }
  }, [memoryFragment, onClose]);

  return (
    <AnimatePresence>
      {memoryFragment && (
        <motion.div
          className="memory-fragment-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: CONSTANTS.MEMORY_FRAGMENTS.fadeInDuration / 1000 }}
        >
          <motion.div
            className={`memory-fragment ${isGlitching ? 'glitching' : ''}`}
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ 
              type: 'spring', 
              stiffness: 300, 
              damping: 30,
              duration: CONSTANTS.MEMORY_FRAGMENTS.fadeInDuration / 1000
            }}
          >
            <div className="fragment-header">
              <div className="fragment-title">{memoryFragment.title}</div>
              <div className="fragment-status">MEMORY RECOVERED</div>
            </div>
            <div className="fragment-content">
              <p className="fragment-text">{memoryFragment.content}</p>
            </div>
            <button
              className="fragment-close-btn"
              onClick={onClose}
            >
              ACKNOWLEDGE
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
